import {Tooltip} from 'platform/components';
import {Box, Clickable, HStack, Icon, Show, Text} from 'platform/foundation';
import styled, {css, keyframes} from 'styled-components';

import {KeyboardEvent, RefObject} from 'react';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {TelephonyCallIndicatorAppearance} from '../../../utils/getTelephonyCallIndicatorAppearance';

interface TelephonyCallIndicatorChipProps extends RequiredTestIdProps {
  appearance: TelephonyCallIndicatorAppearance;
  isCompact: boolean;
  isOpen: boolean;
  /** The call bar just landed here: pulse once. */
  isArriving: boolean;
  /** Tell the viewer once per session that the call controls moved here. */
  isHintShown: boolean;
  /** The element the call bar flies into. */
  frameRef: RefObject<HTMLSpanElement | null>;
  onToggle: (isFromKeyboard: boolean) => void;
}

const CHIP_HEIGHT = 8;
const TOGGLE_KEYS = ['Enter', ' '];
const DOT_SIZE = 2;
const ICON_SIZE = 4;
const ARRIVE_PULSE_MS = 600;
const ARRIVE_PULSE_SCALE = 1.08;

/**
 * The top-bar chip: status dot, phone icon and, unless compact, the state label. Compact mode is
 * for narrow windows and the mobile navigation, where the label moves into a tooltip.
 */
export function TelephonyCallIndicatorChip(props: TelephonyCallIndicatorChipProps) {
  // Enter and Space toggle the call panel like a click.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (TOGGLE_KEYS.includes(event.key)) {
      event.preventDefault();
      props.onToggle(true);
    }
  };

  return (
    <ChipFrame ref={props.frameRef} $isArriving={props.isArriving}>
      <Tooltip
        // The tooltip reads `isOpen` once on mount; a new key opens and later closes the hint.
        key={props.isHintShown ? 'hint' : 'label'}
        label={
          props.isHintShown ? i18n.t('page.telephony.bar.controlsHere') : props.appearance.label
        }
        isOpen={props.isHintShown || undefined}
        isDisabled={!props.isCompact && !props.isHintShown}
        placement="bottom"
      >
        <Clickable
          role="button"
          tabIndex={0}
          aria-label="topBar telephonyCall"
          aria-expanded={props.isOpen}
          onKeyDown={handleKeyDown}
          onClick={() => props.onToggle(false)}
          data-testid={suffixTestId('chip', props)}
        >
          <Box
            height={CHIP_HEIGHT}
            paddingHorizontal={props.isCompact ? 2 : 3}
            borderRadius="small"
            border="1px solid"
            borderColor={props.appearance.borderColor}
            backgroundColor={props.appearance.backgroundColor}
          >
            <HStack height="100%" align="center" spacing={2}>
              <Box
                width={DOT_SIZE}
                height={DOT_SIZE}
                borderRadius="circular"
                flexShrink={0}
                backgroundColor={props.appearance.dotColor}
                data-testid={suffixTestId('dot', props)}
              />
              <Icon value="communication/call" size={ICON_SIZE} color={props.appearance.dotColor} />
              <Show when={!props.isCompact}>
                <Text
                  size="small"
                  color={props.appearance.textColor}
                  noWrap
                  data-testid={suffixTestId('label', props)}
                >
                  {props.appearance.label}
                </Text>
                <Icon
                  value={props.isOpen ? 'navigation/arrow_drop_up' : 'navigation/arrow_drop_down'}
                  size={ICON_SIZE}
                  color={props.appearance.dotColor}
                />
              </Show>
            </HStack>
          </Box>
        </Clickable>
      </Tooltip>
    </ChipFrame>
  );
}

const arrivePulse = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(${ARRIVE_PULSE_SCALE}); }
  100% { transform: scale(1); }
`;

const ChipFrame = styled.span<{$isArriving: boolean}>`
  display: inline-flex;
  ${({$isArriving}) =>
    $isArriving &&
    css`
      animation: ${arrivePulse} ${ARRIVE_PULSE_MS}ms ease-out;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
